import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  ArrowRight,
  Clock,
  Heart,
  AlertCircle,
  Shield,
  Moon,
  Zap,
  HeartPulse,
} from "lucide-react";
import { getAssessmentsList } from "../../data/assessments";

// Get available assessments from data file
const AVAILABLE_ASSESSMENTS = getAssessmentsList().map(assessment => {
  // Calculate estimated time based on question count
  const questionCount = assessment.id === 'anxiety' ? 7 :
    assessment.id === 'depression' ? 9 :
      assessment.id === 'stress' ? 10 :
        assessment.id === 'ptsd' ? 5 :
          assessment.id === 'insomnia' ? 7 :
            assessment.id === 'suicidal' ? 6 : 7;

  const time = questionCount <= 5 ? '2-3 min' :
    questionCount <= 7 ? '3-5 min' :
      questionCount <= 10 ? '5-7 min' : '7-10 min';

  const gradient = assessment.color === 'blue' ? 'from-uzima-maroon-500 to-uzima-maroon-600' :
    assessment.color === 'purple' ? 'from-uzima-maroon-500 to-uzima-maroon-600' :
      assessment.color === 'orange' ? 'from-uzima-maroon-500 to-uzima-maroon-600' :
        assessment.color === 'indigo' ? 'from-uzima-maroon-500 to-uzima-maroon-600' :
          'from-uzima-maroon-500 to-uzima-maroon-600';

  const cardImage = assessment.id === 'anxiety' ? '/images/therapysession1.jpg' :
    assessment.id === 'depression' ? '/images/therapysession2.jpg' :
      assessment.id === 'stress' ? '/images/therapysession3.jpg' :
        assessment.id === 'ptsd' ? '/images/grouptherapy.jpg' :
          assessment.id === 'insomnia' ? '/images/mentalhealthmattersbanner.jpg' :
            assessment.id === 'suicidal' ? '/images/therapysession3.jpg' :
              '/images/therapysession1.jpg';

  return {
    ...assessment,
    questions: questionCount,
    time,
    gradient,
    cardImage,
    shortDesc: assessment.description,
  };
});

const QuizSelector = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAssessments = AVAILABLE_ASSESSMENTS.filter(
    (assessment) =>
      assessment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.shortDesc.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
          Mental Health Self-Assessments
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
          Take evidence-based screening questionnaires to better understand your mental health.
          These are not diagnostic tools but can help you identify areas where professional support may be beneficial.
        </p>

        {/* Search Bar */}
        <div className="max-w-md mx-auto">
          <input
            type="text"
            placeholder="Search assessments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-6 py-3 border-2 border-gray-300 rounded-xl focus:border-uzima-maroon-500 focus:ring-2 focus:ring-uzima-maroon-200 transition"
          />
        </div>
      </div>

      {/* Assessment Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredAssessments.map((assessment, index) => {
          const Icon = assessment.icon;
          return (
            <motion.div
              key={assessment.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="group"
            >
              <Link
                to={`/assessment/${assessment.id}`}
                className="block h-full"
              >
                <div className="relative h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group-hover:-translate-y-1 flex flex-col">
                  {/* Image Header */}
                  <div className="relative h-48 w-full overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex-shrink-0">
                    <img
                      src={assessment.cardImage}
                      alt={assessment.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                    {/* Icon Badge */}
                    <div className={`absolute top-4 right-4 bg-gradient-to-br ${assessment.gradient} w-14 h-14 rounded-xl flex items-center justify-center shadow-lg`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    {/* Title */}
                    <h3 className="text-xl font-display font-bold text-gray-900 mb-2 group-hover:text-uzima-maroon-600 transition-colors">
                      {assessment.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-2">
                      {assessment.shortDesc}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-4 pb-4 border-b border-gray-200">
                      <span className="flex items-center gap-1.5">
                        <Brain className="w-4 h-4 text-uzima-maroon-500" />
                        {assessment.questions} questions
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-uzima-white-500" />
                        {assessment.time}
                      </span>
                    </div>

                    {/* Take Assessment Button */}
                    <div className={`w-full bg-gradient-to-r ${assessment.gradient} text-white font-semibold py-3 px-4 rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 group-hover:gap-3 mt-auto`}>
                      Start Assessment
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* No Results */}
      {filteredAssessments.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            No assessments found matching "{searchTerm}"
          </p>
        </div>
      )}

      {/* Important Notice */}
      <div className="mt-12 bg-gradient-to-r from-uzima-maroon-50 to-uzima-white-50 border border-uzima-maroon-200 p-6 rounded-2xl max-w-4xl mx-auto shadow-sm">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-uzima-maroon-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-uzima-maroon-900 mb-2">Important Information</h4>
            <ul className="text-sm text-uzima-maroon-800 space-y-1.5 list-disc list-inside">
              <li>These screening tools are for informational purposes only and do not constitute medical advice.</li>
              <li>They cannot replace a professional evaluation by a qualified mental health professional.</li>
              <li>If you're experiencing a mental health emergency, please contact emergency services (999/112) or Befrienders Kenya (0800 720 648) immediately.</li>
              <li>For accurate diagnosis and treatment, please consult with a licensed counselor or mental health professional.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizSelector;
